/**
 * useAssessmentSession — Extracted from App.tsx (§1.1)
 *
 * Encapsulates all assessment-related state and handlers:
 * - Blueprint generation, attempt tracking, report building
 * - Session recovery (crash resilience)
 * - Per-question timing
 *
 * Returns questions for App.tsx to feed into PracticeSession;
 * App.tsx still owns view/navigation transitions.
 */

import { useState, useRef, useEffect } from 'react'
import { Question, ModuleId, FamilyMasteryState } from '../types'
import { GroupId } from '../config/systemConfig'
import {
  AssessmentConfig,
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentAnswer,
  AssessmentReport,
} from '../components/assessment/types'
import { generateAssessmentBlueprint } from '../components/assessment/utils/assessmentBlueprintGenerator'
import {
  saveBlueprint,
  saveAttempt,
  saveReport,
} from '../components/assessment/utils/assessmentStorage'
import { buildAssessmentReport } from '../components/assessment/utils/assessmentReportBuilder'
import { getQuestionWithExplanation } from '../data/questionLoader'
import {
  saveSessionState,
  getRecoverableSession,
  clearSessionState,
  RecoverableSession,
} from '../components/assessment/utils/assessmentSessionRecovery'
import { error as logError } from '../utils/logger'

interface UseAssessmentSessionParams {
  familyMastery: Record<string, FamilyMasteryState>
  selectedGroup: GroupId
  recordAnswer: (
    module: ModuleId,
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
    payload?: string,
    errorType?: string,
    delta?: number,
  ) => void
  allQuestions: Question[]
  /** Called once on mount if a recoverable session is found */
  isReady: boolean
}

interface AssessmentStartResult {
  questions: Question[]
}

export function useAssessmentSession({
  familyMastery,
  selectedGroup,
  recordAnswer,
  allQuestions,
  isReady,
}: UseAssessmentSessionParams) {
  // ── State ──────────────────────────────────────────────────────────
  const [assessmentBlueprint, setAssessmentBlueprint] =
    useState<AssessmentBlueprint | null>(null)
  const [assessmentAttempt, setAssessmentAttempt] =
    useState<AssessmentAttempt | null>(null)
  const [assessmentStartTime, setAssessmentStartTime] = useState<number>(0)
  const [preFamilyMastery, setPreFamilyMastery] = useState<
    Record<string, FamilyMasteryState>
  >({})
  const questionStartTimeRef = useRef<number>(Date.now())
  const [assessmentReport, setAssessmentReport] =
    useState<AssessmentReport | null>(null)
  const [recoverableSession, setRecoverableSession] =
    useState<RecoverableSession | null>(null)
  const [assessmentError, setAssessmentError] = useState<string | null>(null)

  // ── Recovery check on mount ────────────────────────────────────────
  useEffect(() => {
    if (isReady && !assessmentBlueprint) {
      const session = getRecoverableSession()
      if (session) {
        setRecoverableSession(session)
      }
    }
  }, [isReady])

  // ── Handlers ───────────────────────────────────────────────────────

  const startAssessment = (
    config: AssessmentConfig,
  ): AssessmentStartResult | null => {
    // Capture pre-assessment mastery state (deep clone)
    let preMastery: Record<string, FamilyMasteryState> = {}
    try {
      preMastery = JSON.parse(JSON.stringify(familyMastery || {}))
    } catch (err) {
      logError('Failed to clone familyMastery:', err)
    }
    setPreFamilyMastery(preMastery)

    // Generate blueprint
    const blueprint = generateAssessmentBlueprint(
      config,
      familyMastery || {},
      selectedGroup,
    )

    if (blueprint.questions.length === 0) {
      setAssessmentError(
        'Geen vragen beschikbaar voor deze configuratie. Probeer andere vakken te selecteren.',
      )
      return null
    }

    // Save blueprint
    saveBlueprint(blueprint)
    setAssessmentBlueprint(blueprint)

    // Create attempt
    const attempt: AssessmentAttempt = {
      id: `attempt_${Date.now()}`,
      blueprintId: blueprint.id,
      startedAt: Date.now(),
      completedAt: null,
      selectedGroup,
      answers: [],
      countsForMastery: config.countsForMastery,
      totalTimeSeconds: 0,
      status: 'in-progress',
    }
    setAssessmentAttempt(attempt)
    setAssessmentStartTime(Date.now())
    questionStartTimeRef.current = Date.now()

    // Save initial session state for recovery
    saveSessionState(blueprint, attempt, Date.now(), preMastery)

    // Build questions for PracticeSession
    const questions = blueprint.questions
      .map((bq) => getQuestionWithExplanation(bq.questionId))
      .filter((q): q is Question => q !== null)

    return { questions }
  }

  const completeAssessment = (): void => {
    if (!assessmentAttempt || !assessmentBlueprint) return

    const totalTimeSeconds = Math.floor(
      (Date.now() - assessmentStartTime) / 1000,
    )

    const completedAttempt: AssessmentAttempt = {
      ...assessmentAttempt,
      completedAt: Date.now(),
      totalTimeSeconds,
      status: 'completed',
    }
    saveAttempt(completedAttempt)

    const report = buildAssessmentReport(
      completedAttempt,
      assessmentBlueprint,
      preFamilyMastery,
      familyMastery || {},
    )
    saveReport(report)

    setAssessmentReport(report)
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
    clearSessionState()
  }

  const handleAnswer = (
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
    payload?: string,
    errorType?: string,
    delta?: number,
  ): void => {
    if (!assessmentAttempt || !assessmentBlueprint) return

    const question = allQuestions.find((q) => q.id === questionId)
    if (!question) return

    const blueprintQuestion = assessmentBlueprint.questions.find(
      (bq) => bq.questionId === questionId,
    )
    if (!blueprintQuestion) return

    // Calculate time spent using per-question timer
    const now = Date.now()
    const timeSpent = Math.floor((now - questionStartTimeRef.current) / 1000)
    questionStartTimeRef.current = now

    const answer: AssessmentAnswer = {
      questionId,
      userChoice,
      isCorrect,
      timeSpent: Math.max(1, timeSpent),
      familyId: blueprintQuestion.familyId,
      module: blueprintQuestion.module,
    }

    const updatedAttempt = {
      ...assessmentAttempt,
      answers: [...assessmentAttempt.answers, answer],
    }
    setAssessmentAttempt(updatedAttempt)

    // Persist for crash recovery
    saveSessionState(
      assessmentBlueprint,
      updatedAttempt,
      assessmentStartTime,
      preFamilyMastery,
    )

    // Optionally update mastery
    if (assessmentAttempt.countsForMastery) {
      recordAnswer(
        question.module,
        questionId,
        isCorrect,
        userChoice,
        payload,
        errorType,
        delta,
      )
    }
  }

  const resumeAssessment = (): AssessmentStartResult | null => {
    if (!recoverableSession) return null

    const {
      blueprint,
      attempt,
      startTime,
      preFamilyMastery: preMastery,
    } = recoverableSession

    setAssessmentBlueprint(blueprint)
    setAssessmentAttempt(attempt)
    setAssessmentStartTime(startTime)
    setPreFamilyMastery(preMastery)

    // Build remaining questions (skip already answered)
    const answeredIds = new Set(attempt.answers.map((a) => a.questionId))
    const remainingQuestions = blueprint.questions
      .filter((bq) => !answeredIds.has(bq.questionId))
      .map((bq) => getQuestionWithExplanation(bq.questionId))
      .filter((q): q is Question => q !== null)

    setRecoverableSession(null)

    if (remainingQuestions.length === 0) {
      clearSessionState()
      return null
    }

    return { questions: remainingQuestions }
  }

  const abandonRecovery = (): void => {
    if (recoverableSession) {
      const abandonedAttempt: AssessmentAttempt = {
        ...recoverableSession.attempt,
        completedAt: Date.now(),
        totalTimeSeconds: Math.floor(
          (Date.now() - recoverableSession.startTime) / 1000,
        ),
        status: 'abandoned',
      }
      saveAttempt(abandonedAttempt)
    }
    clearSessionState()
    setRecoverableSession(null)
  }

  const abandonCurrentAssessment = (): void => {
    if (assessmentAttempt) {
      const abandonedAttempt: AssessmentAttempt = {
        ...assessmentAttempt,
        completedAt: Date.now(),
        totalTimeSeconds: Math.floor((Date.now() - assessmentStartTime) / 1000),
        status: 'abandoned',
      }
      saveAttempt(abandonedAttempt)
      clearSessionState()
    }
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
  }

  const clearAssessmentState = (): void => {
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
    setAssessmentReport(null)
    setAssessmentError(null)
  }

  return {
    // State
    assessmentBlueprint,
    assessmentAttempt,
    assessmentReport,
    assessmentError,
    recoverableSession,
    assessmentStartTime,

    // Handlers
    startAssessment,
    completeAssessment,
    handleAnswer,
    resumeAssessment,
    abandonRecovery,
    abandonCurrentAssessment,
    clearAssessmentState,

    // Setters (for modals)
    setAssessmentError,
    setAssessmentReport,
  }
}
